import { AnimatePresence, motion } from "framer-motion";
import { Check, Swords } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ACTIONS, ROLES } from "../gameData";
import type { GameState, Player, Role } from "../types";
import { RoleCard } from "./RoleCard";

/** Longest player name allowed inside an event headline before it is elided. */
const NAME_LIMIT = 13;

function shortName(name: string | undefined, fallback: string) {
  const value = name ?? fallback;
  return value.length > NAME_LIMIT ? `${value.slice(0, NAME_LIMIT - 1)}…` : value;
}

interface InterferenceProps {
  game: GameState;
  me: Player;
  onPass: () => Promise<boolean>;
  onChallenge: () => Promise<boolean>;
  onBlock: (role: Role) => Promise<boolean>;
}

export function InterferencePanel({
  game,
  me,
  onPass,
  onChallenge,
  onBlock
}: InterferenceProps) {
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);
  const action = game.action ? ACTIONS[game.action.type] : null;
  const actor = game.players.find((player) => player.id === game.action?.actorId);
  const target = game.players.find((player) => player.id === game.action?.targetId);
  const claimant = game.players.find((player) => player.id === game.window?.claimantId);
  const claimRole = game.window?.claimRole ? ROLES[game.window.claimRole] : null;
  /** The one window a declared action opens. "claim" is only ever a block. */
  const isResponse = game.phase === "response_window";
  const eventText = describeEvent(game, actor, target, claimant);

  // Eligibility is NOT one set. `eligibleIds` is the union — everyone who must
  // answer for the window to close — but the two answers it can be closed with
  // have different rosters:
  //   DOUBT  any living opponent, whenever the actor claimed a role.
  //   BLOCK  every opponent on Foreign Aid; the TARGET ALONE on Steal and
  //          Assassinate.
  // A bystander on a Steal is therefore eligible for the window and eligible to
  // doubt, but must not be offered a block. One Pass surrenders both.
  const eligible = Boolean(game.window?.eligibleIds.includes(me.id));
  const passed = Boolean(game.window?.passes.includes(me.id));
  // A block declared in this window is HELD while the doubt against the actor
  // settles, so the window outlives it. Two consequences the panel must honour:
  // the blocker has spent their answer and gets no live controls, and everyone
  // still deciding can see the block that is already on the table.
  const heldBlockRole = game.window?.blockRole ?? null;
  const blocker = game.players.find((player) => player.id === game.window?.blockerId);
  const iBlocked = Boolean(game.window?.blockerId) && game.window?.blockerId === me.id;
  // Once the bar empties the server auto-passes for everyone still silent.
  // The client only REFLECTS that — it never submits a pass of its own, so
  // there is no double submission. The controls stay live but go lapsed: the
  // bar can only run behind the true deadline (msLeft is already stale by one
  // network hop), so a tap that still lands is a window the server considers
  // open, and a player is never stranded if the sweep is late.
  const canRespond = eligible && !passed && !iBlocked;
  const canDoubt =
    canRespond &&
    Boolean(game.window?.claimRole) &&
    (game.window?.challengeIds ?? []).includes(me.id);
  const blockRoles = canRespond && (game.window?.blockIds ?? []).includes(me.id)
    ? (game.window?.allowedRoles ?? [])
    : [];
  const canBlock = blockRoles.length > 0;
  // Foreign Aid is the only shape with a block and no doubt, and it claims
  // exactly one role. That role takes the slot Doubt would have occupied — "a
  // little duke where the doubt otherwise would have been". Every other
  // blockable action also offers a doubt, so its blocks go beneath the pair.
  const blockInDoubtSlot = canBlock && !canDoubt && blockRoles.length === 1;
  const blockBeneath = canBlock && !blockInDoubtSlot;

  // The server owns the clock. durationMs is the denominator, msLeft is the
  // remaining time at the instant this snapshot was sent — anchor it against
  // performance.now(), never the device clock. Either may be missing on an
  // older server, in which case no bar is drawn and nothing ever locks.
  const durationMs = game.window?.durationMs ?? 0;
  const msLeft = game.window?.msLeft ?? 0;
  const hasTimer =
    Number.isFinite(durationMs) && durationMs > 0 && Number.isFinite(msLeft);

  // Identity of the current window: changes whenever a new question is asked.
  const windowKey = [
    game.phase,
    game.action?.id ?? "action",
    game.window?.source ?? "action",
    game.window?.claimantId ?? "open",
    game.window?.claimRole ?? "none"
  ].join("|");

  useEffect(() => {
    setSubmitting(false);
    setExpired(false);
  }, [windowKey]);

  // Keyed off the window, not the phase: a response window carries a claim for
  // every action except Foreign Aid, which claims nothing and so has nothing to
  // doubt.
  const eventDetail =
    game.window?.source === "block"
      ? "The block can be challenged"
      : claimRole
        ? // A claim proven against a failed doubt cannot be doubted twice, and
          // the window only reopened to collect the block it still owes. Saying
          // "claims Assassin" here would invite a doubt that no longer exists.
          game.window?.claimProven
          ? `${claimRole.name} proven · block or pass`
          : `${shortName(claimant?.name, "A player")} ${
              claimant?.name.toLowerCase() === "you" ? "claim" : "claims"
            } ${claimRole.name}`
        : canRespond
          ? "You can interfere"
          : "Waiting for responses";

  if (!["response_window", "claim_window"].includes(game.phase) || !game.window || !action) {
    return null;
  }

  const statusText = iBlocked
    ? "You blocked · the claim settles first"
    : passed
      ? "You passed · resolving…"
      : expired
        ? "Time up · resolving…"
        : eventDetail;

  const submit = async (response: () => Promise<boolean>) => {
    if (submitting) return;
    setSubmitting(true);
    const accepted = await response();
    if (!accepted) setSubmitting(false);
  };

  const holds = (role: Role) =>
    me.influence.some((card) => !card.revealed && card.role === role);

  return (
    <motion.div
      className={[
        "interference-event",
        isResponse ? "interference-event--response" : "interference-event--claim",
        expired ? "interference-event--lapsed" : ""
      ]
        .filter(Boolean)
        .join(" ")}
      key={`${game.phase}-${game.action?.id ?? "action"}-${game.window.claimantId ?? "open"}`}
      initial={false}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* One grid, three occupants of column 3: DOUBT, or the lone blocking
          role standing in for it, or nothing at all when the player has
          already answered. */}
      <div className="interference-event__main">
        {canRespond && (
          <motion.button
            className="interference-answer interference-answer--pass"
            type="button"
            disabled={submitting}
            onClick={() => submit(onPass)}
            whileTap={{ scale: 0.94 }}
          >
            <Check size={17} strokeWidth={2.6} />
            <span>Pass</span>
          </motion.button>
        )}

        <div className="interference-event__copy">
          <h2 className="interference-event__headline">{eventText}</h2>
          <p>{statusText}</p>
        </div>

        {canDoubt && (
          <motion.button
            className="interference-answer interference-answer--challenge"
            type="button"
            disabled={submitting}
            onClick={() => submit(onChallenge)}
            whileTap={{ scale: 0.94 }}
          >
            <Swords size={17} strokeWidth={2.6} />
            <span>Doubt</span>
          </motion.button>
        )}

        {blockInDoubtSlot &&
          (() => {
            const role = blockRoles[0];
            const held = holds(role);
            return (
              <motion.button
                className="interference-answer interference-answer--block"
                type="button"
                disabled={submitting}
                onClick={() => submit(() => onBlock(role))}
                whileTap={{ scale: 0.94 }}
                style={{ "--role-color": ROLES[role].color } as React.CSSProperties}
                title={ROLES[role].name}
                aria-label={`Block with ${ROLES[role].name}${held ? ", held" : ", bluff"}`}
              >
                <img className="interference-answer__face" src={ROLES[role].image} alt="" />
                <span>Block</span>
                {!held && <em className="interference-answer__bluff">BLUFF</em>}
              </motion.button>
            );
          })()}
      </div>

      {hasTimer && (
        <WindowTimer
          durationMs={durationMs}
          msLeft={msLeft}
          resetKey={windowKey}
          onEmpty={() => setExpired(true)}
        />
      )}

      {/* Mutually exclusive with the block buttons: the server withdraws the
          block offer the moment one lands, so this strip reuses that slot to
          say what is already on the table while the doubt track runs. */}
      {heldBlockRole && (
        <div
          className="held-block"
          style={{ "--role-color": ROLES[heldBlockRole].color } as React.CSSProperties}
        >
          <img src={ROLES[heldBlockRole].image} alt="" />
          <span>
            <b>
              {shortName(blocker?.name, "A player")}{" "}
              {blocker?.name.toLowerCase() === "you" ? "block" : "blocks"} as{" "}
              {ROLES[heldBlockRole].name}
            </b>
            <small>The claim settles first</small>
          </span>
        </div>
      )}

      {blockBeneath && (
        <div className="inline-block-roles">
          {blockRoles.map((role) => {
            const held = holds(role);
            return (
              <button
                className="inline-block-role"
                type="button"
                key={role}
                disabled={submitting}
                onClick={() => submit(() => onBlock(role))}
                style={{ "--role-color": ROLES[role].color } as React.CSSProperties}
                aria-label={`Block with ${ROLES[role].name}${held ? ", held" : ", bluff"}`}
              >
                <img src={ROLES[role].image} alt="" />
                <span>
                  <b>BLOCK</b>
                  <small>{ROLES[role].name}</small>
                </span>
                {!held && <em className="inline-block-role__bluff">BLUFF</em>}
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

interface TimerProps {
  durationMs: number;
  msLeft: number;
  resetKey: string;
  onEmpty: () => void;
}

/**
 * Depleting bar for challenge/block windows only. Paints straight to the DOM
 * on rAF so a ticking window never re-renders the panel underneath it.
 */
function WindowTimer({ durationMs, msLeft, resetKey, onEmpty }: TimerProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLSpanElement | null>(null);
  const emptyRef = useRef(onEmpty);

  useEffect(() => {
    emptyRef.current = onEmpty;
  });

  useEffect(() => {
    const total = durationMs;
    const begin = Math.max(0, Math.min(msLeft, total));
    const startedAt = performance.now();
    let frame = 0;
    let urgent: boolean | null = null;
    let finished = false;

    const paint = () => {
      const remaining = Math.max(0, begin - (performance.now() - startedAt));
      const ratio = total > 0 ? remaining / total : 0;
      if (fillRef.current) {
        fillRef.current.style.transform = `scaleX(${ratio})`;
      }
      const nextUrgent = ratio <= 0.25;
      if (nextUrgent !== urgent) {
        urgent = nextUrgent;
        if (trackRef.current) {
          trackRef.current.dataset.urgent = nextUrgent ? "true" : "false";
        }
      }
      if (remaining <= 0) {
        if (!finished) {
          finished = true;
          emptyRef.current();
        }
        return;
      }
      frame = requestAnimationFrame(paint);
    };

    paint();
    return () => cancelAnimationFrame(frame);
  }, [durationMs, msLeft, resetKey]);

  return (
    <div className="interference-timer" ref={trackRef} aria-hidden="true">
      <span className="interference-timer__fill" ref={fillRef} />
    </div>
  );
}

interface DecisionProps {
  game: GameState;
  me: Player;
  onLose: (cardId: string) => void;
  onExchange: (keepIds: string[]) => void;
}

export function DecisionPanel({ game, me, onLose, onExchange }: DecisionProps) {
  const [kept, setKept] = useState<string[]>([]);
  const hiddenCount = me.influence.filter((card) => !card.revealed).length;
  const exchangeCards = game.exchangeChoices?.cards ?? [];
  const challenger = game.players.find(
    (player) => player.id === game.pendingLoss?.challengerId
  );
  const calledOutBy =
    game.pendingLoss?.cause.startsWith("Bluffed") && challenger
      ? challenger
      : null;

  const panel = useMemo(() => {
    if (game.phase === "lose_influence" && game.pendingLoss?.playerId === me.id) {
      return "loss";
    }
    if (game.phase === "exchange" && game.exchangeChoices?.playerId === me.id) {
      return "exchange";
    }
    return null;
  }, [game, me.id]);

  if (!panel) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.section
        className={`decision-panel decision-panel--${panel}`}
        key={`${panel}-${game.action?.id ?? game.turn}`}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12 }}
      >
        {panel === "loss" && (
          <>
            {calledOutBy && (
              <motion.div
                className="loss-callout"
                initial={{ opacity: 0, y: 10, rotate: -1 }}
                animate={{ opacity: 1, y: 0, rotate: -2 }}
              >
                <Swords size={17} />
                <b>{calledOutBy.name} called you out</b>
              </motion.div>
            )}
            <span className="eyebrow">Influence at stake</span>
            <h2>Choose what you lose</h2>
            <p>{game.pendingLoss?.cause}</p>
            <p className="decision-hint">Hold a card to read what it does.</p>
            <div className="decision-cards">
              {me.influence
                .filter((card) => !card.revealed)
                .map((card) => (
                  <RoleCard
                    key={card.id}
                    card={card}
                    variant="loss"
                    selectable
                    onSelect={() => onLose(card.id)}
                  />
                ))}
            </div>
          </>
        )}

        {panel === "exchange" && (
          <>
            <span className="eyebrow">The Ambassador returns</span>
            <h2>Keep {hiddenCount}</h2>
            <p>Tap {hiddenCount} to keep.</p>
            <p className="decision-hint">Hold a card to read what it does.</p>
            <div className="exchange-grid">
              {exchangeCards.map((card) => (
                <RoleCard
                  key={card.id}
                  card={card}
                  variant="exchange"
                  selectable
                  selected={kept.includes(card.id)}
                  onSelect={() =>
                    setKept((current) =>
                      current.includes(card.id)
                        ? current.filter((id) => id !== card.id)
                        : current.length < hiddenCount
                          ? [...current, card.id]
                          : current
                    )
                  }
                />
              ))}
            </div>
            <button
              className="button button--primary button--full"
              type="button"
              disabled={kept.length !== hiddenCount}
              onClick={() => {
                onExchange(kept);
                setKept([]);
              }}
            >
              Keep these cards
            </button>
          </>
        )}
      </motion.section>
    </AnimatePresence>
  );
}

function describeEvent(
  game: GameState,
  actor: Player | undefined,
  target: Player | undefined,
  claimant: Player | undefined
) {
  if (game.window?.source === "block") {
    const role = game.window.claimRole ? ROLES[game.window.claimRole].name : "a role";
    const isYou = (claimant?.name ?? "").toLowerCase() === "you";
    const subject = shortName(claimant?.name, "A player");
    return `${subject} ${isYou ? "block" : "blocks"} as ${role}`;
  }

  const isYou = (actor?.name ?? "").toLowerCase() === "you";
  const subject = shortName(actor?.name, "A player");
  const victim = shortName(target?.name, "a rival");
  switch (game.action?.type) {
    case "patronage":
      return `${subject} ${isYou ? "take" : "takes"} Foreign Aid`;
    case "levy":
      return `${subject} ${isYou ? "take" : "takes"} Tax`;
    case "exchange":
      return `${subject} ${isYou ? "exchange" : "exchanges"} Influence`;
    case "seize":
      return `${subject} ${isYou ? "steal" : "steals"} from ${victim}`;
    case "silence":
      return `${subject} ${isYou ? "assassinate" : "assassinates"} ${victim}`;
    default:
      return `${subject} ${isYou ? "make" : "makes"} a move`;
  }
}
